// @flow

import { compose, always, last, split } from 'ramda';
import startOfMonth from 'date-fns/start_of_month';
import startOfYear from 'date-fns/start_of_year';
import addMonths from 'date-fns/add_months';
import subYears from 'date-fns/sub_years';
import max from 'date-fns/max';
import {
  getISOIntervalByLen,
  oneYearBack,
  previousPeriod,
  getInterval,
  recurring,
  intervalInside,
  convertToYearlyPrecision,
} from './time';
import { Timespan, Precision, Compare } from '../constants';

const yearStart = compose(startOfYear, last, split('/'));

/*
 * Start month should be one month back to avoid incomplete data.
 */
const monthStart = timeBounds =>
  compose(startOfMonth, last, split('/'))(timeBounds);
//return addMonths(thisMonth, -2);

export const timespanPresets = {
  [Timespan.LAST_MONTH]: timeBounds =>
    getISOIntervalByLen(monthStart(timeBounds), 1),

  [Timespan.LAST_12_MONTHS]: timeBounds =>
    getISOIntervalByLen(addMonths(monthStart(timeBounds), -11), 12),

  [Timespan.THIS_YEAR]: timeBounds =>
    getInterval(yearStart(timeBounds), monthStart(timeBounds)),

  [Timespan.LAST_YEAR]: timeBounds =>
    getISOIntervalByLen(subYears(yearStart(timeBounds), 1), 12),

  [Timespan.LAST_5_YEARS]: timeBounds =>
    getISOIntervalByLen(subYears(yearStart(timeBounds), 5), 5 * 12),

  [Timespan.FROM_2010]: timeBounds =>
    getInterval('2010-01-01T00:00:00', addMonths(yearStart(timeBounds), -1)),

  [Timespan.FROM_2000]: timeBounds =>
    getInterval('2000-01-01T00:00:00', addMonths(yearStart(timeBounds), -1)),

  [Timespan.FROM_1990]: timeBounds =>
    getInterval('1990-01-01T00:00:00', addMonths(yearStart(timeBounds), -1)),

  [Timespan.CUSTOM]: timeBounds =>
    getISOIntervalByLen(addMonths(monthStart(timeBounds), -11), 12),
};

export const timespanPrecisionPresets = {
  [Timespan.LAST_MONTH]: Precision.MONTHLY,
  [Timespan.LAST_12_MONTHS]: Precision.MONTHLY,
  [Timespan.THIS_YEAR]: Precision.MONTHLY,
  [Timespan.LAST_YEAR]: Precision.YEARLY,
  [Timespan.LAST_5_YEARS]: Precision.YEARLY,
  [Timespan.FROM_2010]: Precision.YEARLY,
  [Timespan.FROM_2000]: Precision.YEARLY,
  [Timespan.FROM_1990]: Precision.YEARLY,
};

export const comparePresets = {
  [Compare.NONE]: always([]),

  [Compare.YEAR]: interval => [oneYearBack(interval)],

  [Compare.PERIOD]: interval => [previousPeriod(interval)],

  [Compare.RECURRING]: (interval, timeBounds) =>
    recurring(timeBounds, interval),

  /*
   * The following function should return admissible
   * (i.e. which lies inside timeBounds)
   * default custom compare interval. First, it tries
   * previous period. Secondly, the perod which starts
   * from the beginning of timeBounds. In case, if selected
   * interval starts exactly from the beginning of timeBounds,
   * it returns just single year starting since the beginning
   * of timeBounds. It is important to return year, because
   * the precision may be Yearly.
   */
  [Compare.CUSTOM]: (interval, timeBounds) => {
    const previousInterval = previousPeriod(interval);
    if (intervalInside(previousInterval, timeBounds)) {
      return [previousInterval];
    }

    const [boundsMin] = timeBounds.split('/');
    return [
      convertToYearlyPrecision(
        getInterval(boundsMin, max(boundsMin, previousInterval.split('/')[1])),
        timeBounds,
      ),
    ];
  },
};



// WEBPACK FOOTER //
// ./src/utils/timePresets.js